import React, { useEffect, useRef, useState, MutableRefObject } from 'react'
import { LottieRefCurrentProps } from 'lottie-react'
import { useIntersections } from '@/hooks/Intersections'
import { usePinable } from '@/hooks/Pinable'

export function useLazyLottieAnimations(numberOfRows: number, pinOffset = 5) {
  const lottieRefs = useRef<MutableRefObject<LottieRefCurrentProps | null>[]>([])
  const rowRefs = useRef<MutableRefObject<HTMLDivElement | null>[]>([])
  const [animsLoaded, setAnimsLoaded] = useState(false)
  const [animsToLoad, setAnimsToLoad] = useState([0, 0, 0, 0])

  // Build refs
  useEffect(() => {
    for (let i = 0; i < numberOfRows; i++) {
      lottieRefs.current[i] = lottieRefs.current[i] || React.createRef()
      rowRefs.current[i] = rowRefs.current[i] || React.createRef()
    }
    return () => {
      for (let i = 0; i < numberOfRows; i++) {
        lottieRefs.current[i].current?.destroy?.()
        rowRefs.current[i].current = null
      }
    }
  }, [numberOfRows])

  // Intersection and pin logic
  const intersections = useIntersections(rowRefs.current, { threshold: 0.5 })
  const isOffsetReached = usePinable(pinOffset)

  // Lazy load animations
  useEffect(() => {
    if (isOffsetReached && !animsLoaded) {
      setAnimsToLoad([1, 0, 0, 0])
      setTimeout(() => setAnimsToLoad([1, 1, 0, 0]), 600)
      setTimeout(() => setAnimsToLoad([1, 1, 1, 0]), 1500)
      setTimeout(() => {
        setAnimsToLoad([1, 1, 1, 1])
        setAnimsLoaded(true)
      }, 2000)
    }
  }, [isOffsetReached, animsLoaded])

  // Play/pause based on intersection
  useEffect(() => {
    if (!intersections.length) return
    intersections.forEach((intersection, i) => {
      if (intersection) {
        lottieRefs.current[i].current?.play?.()
      } else {
        lottieRefs.current[i].current?.pause?.()
      }
    })
  }, [intersections])

  return { lottieRefs, rowRefs, animsToLoad }
}
